import {Injectable} from "@angular/core";
import {AsyncSubject, Observable, Subject, throwError} from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {NetworkService} from "./network.service";
import {StartupHttpService} from "../http/startup.http.service";
import {ClientConfiguration} from "../configuration/client.config";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {IUserModel} from "../usermodel/usermodel.objects";
import {EventBus} from "../../event/EventBus";
import {UserModelUpdateReceivedEvent} from "../../event/UserModelEvents";
import {ModalConfig} from "../notification/modal.config";
import {Login} from "../../login/login";
import {of} from "rxjs/internal/observable/of";
import {catchError, map} from "rxjs/operators";
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {ErrorType, Scilearn} from "../../constants";
import {IOptions} from "../http/http";

@Injectable()
export class UploadService {

    constructor(private config: ClientConfiguration,
                private http: HttpClient,
                private networkService: NetworkService,
                private eventBus: EventBus,
                private log: BugsnagLoggingService,
                private modalConfig: ModalConfig,
                private login: Login,
                private startupHttpService: StartupHttpService) {
        this.uploadQueue = [];
        this.uploading = false;
        this._allowUpload = true;
        this._allowRetry = true;
        this.offLineStartTime = 0;
    }

    public get allowUpload(): boolean {
        return this._allowUpload;
    }

    public static UPLOAD_RETRY_INTERVAL = 3000;
    public static MAX_RETRIES = 5;
    public static WRONG_SENTENCE_ID_REGEXP = /wrong sentence id (\d+), expected id is (\d+)/;

    private uploadQueue: QueueItem[];
    private uploading: boolean;
    protected _allowUpload: boolean;
    protected _allowRetry: boolean;
    private offLineStartTime: number;
    private lastOpenBookSession = "";
    private CONTENT_TYPE_FORM_URLENCODED = { "Content-Type": "application/x-www-form-urlencoded" };
    private SLC_RESPONSE_SUCCESS = 1;

    private static asyncRequestParse(obj: Object): string {
        let str = [];
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                let value = obj[key];
                if (value instanceof Array) {
                    for (let i = 0; i < value.length; i++) {
                        let subObj = value[i];
                        for (let subKey in subObj) {
                            if (subObj.hasOwnProperty(subKey)) {
                                str.push(encodeURIComponent(key) + "[" + i + "][" + encodeURIComponent(subKey) + "]=" +
                                         encodeURIComponent(subObj[subKey]));
                            }
                        }
                    }
                } else {
                    str.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
                }
            }
        }
        return str.join("&");
    }

    private getOptions(headers?): IOptions {
        return ({
            withCredentials: true,
            headers: new HttpHeaders({ "X-Forwarded-Host": this.config.slcReferrerDomain, ...(headers || {}) })
        });
    }


    // TODO: these methods all return success even if the response returned is basically a handled error from SLC.  There
    //   are definitely some cases where the client should probably do something different based on a non-success StatusCode
    private postData(data: object, url: string): Observable<APIResult> {
        return this.http.post(url, UploadService.asyncRequestParse(data), this.getOptions(this.CONTENT_TYPE_FORM_URLENCODED))
            .pipe(map((response: SLCResponse) => {
                return {data: response, success: true, status: 1} as APIResult;
            }),
                catchError((errorResponse: HttpErrorResponse) => {
                    return throwError({data: errorResponse.statusText, success: false, status: errorResponse.status} as APIResult);
                }));
    }

    private putData(url: string): Observable<APIResult> {
        return this.http.put(url, "", this.getOptions(this.CONTENT_TYPE_FORM_URLENCODED)).pipe(map((response: Response) => {
            return {data: response, success: true, status: 1} as APIResult;
        }),
            catchError((errorResponse: HttpErrorResponse) => {
                return throwError({data: errorResponse.statusText, success: false, status: errorResponse.status} as APIResult);
            }));
    }

    private deleteData(url: string): Observable<APIResult> {
        return this.http.delete(url, this.getOptions())
            .pipe(map((response: SLCResponse) => {
                return {data: response, success: true, status: 1} as APIResult;
            }), catchError((errorResponse: HttpErrorResponse) => {
                return throwError({data: errorResponse.statusText, success: false, status: errorResponse.status} as APIResult);
            }));
    }

    private startNextUpload(): void {
        this.updateOfflineStatus();
        this.shiftQueue();
        this.uploadFirstQueueItem();
    }

    private updateOfflineStatus(): void {
        if (this.networkService.isOnline()) {
            this.offLineStartTime = undefined;
        } else {
            this.offLineStartTime = Date.now();
        }
    }

    /**
     * If asynchronous, observable will complete immediately.
     * If synchronous, observable will not complete until queue entry has been transmitted.
     */
    private addQueueItem(queueItem: QueueItem): Observable<string> {

        let s = new AsyncSubject<string>();
        if (queueItem.isAsync) {
          // if async (non-blocking), this will instead happen when upload completes
            s.next("queued async");
            s.complete();
        } else {
            // we save the subject for later if we're blocking
            queueItem.subject = s;
        }

        this.uploadQueue.push(queueItem);

        if (!this.uploading) {
            this.uploadFirstQueueItem();
        }

        return s;
    }

    private getFirstQueueItem(): QueueItem {
        return (this.uploadQueue.length > 0) ? this.uploadQueue[0] : undefined;
    }

    private shiftQueue(): void {
        this.uploadQueue.shift();
    }

    private uploadFirstQueueItem(): void {
        if (this.uploadQueue.length > 0) {
            this.uploadQueueItem(this.getFirstQueueItem());
        } else {
            this.uploading = false;
        }
    }

    private uploadQueueItem(queueItem: QueueItem): void {

        this.uploading = true;

        this.log.log(Date.now() + "  posting data of type: " + queueItem.type);

        switch (queueItem.type) {
        case TYPE.CLOSE_SESSION:
        case TYPE.CLOSE_BOOK_SESSION:
            if (queueItem.synchronous) {
                this.startupHttpService.deleteWithReferrer(queueItem.url, (result) => {
                    this.successfulUpload(queueItem, result);
                }, (result) => {
                    this.unsuccessfulUpload(queueItem, result);
                });
            } else {
                this.deleteData(queueItem.url)
                    .subscribe((result: APIResult) => {
                        if (result.success) {
                            this.successfulUpload(queueItem, result);
                        } else {
                            this.unsuccessfulUpload(queueItem, result);
                        }
                    }, (result: HttpErrorResponse) => {
                        this.unsuccessfulUpload(queueItem, {data: result, success: false, status: result.status});
                    });
            }
            break;

        default:
            if (queueItem.synchronous) {
                this.startupHttpService.post(queueItem.url, (result) => {
                    this.successfulUpload(queueItem, result);
                }, (result) => {
                    this.unsuccessfulUpload(queueItem, result);
                });
            } else {
                this.postData(queueItem.data, queueItem.url)
                    .subscribe((result: APIResult) => {
                        if (result.success) {
                            this.successfulUpload(queueItem, result);
                        } else {
                            this.unsuccessfulUpload(queueItem, result);
                        }
                    }, (result: APIResult) => {
                        this.unsuccessfulUpload(queueItem, result);
                    });
            }
        }
    }

    private successfulUpload(queueItem: QueueItem, result: APIResult): void {
        queueItem.result = result;
        try {
            if (result && result.data && result.data.hasOwnProperty("UserModel")) {

                let model: IUserModel = (result.data as any).UserModel;
                this.eventBus.next(new UserModelUpdateReceivedEvent(model));
            }

            if (!queueItem.isAsync) {
                queueItem.subject.next(result.data);
                queueItem.subject.complete();
            }
            if (typeof queueItem.success === "function") {
                queueItem.success(result.data);
            }
        } catch (e) {
            this.log.error("-----------------------Error during upload success handler:" + e.name, ErrorType.CONNECTION);
        } finally {
            this.startNextUpload();
        }
    }

    private isTrainingSessionException(result: any) {
        return result instanceof String && result.match(/StatusCode=\d+/)[0] === "-102";
    }

    private unsuccessfulUpload(queueItem: QueueItem, result: APIResult): void {
        queueItem.result = result;

        if (!this.networkService.isOnline()) {
            this.log.error("result on unsuccessful upload " + result.status, ErrorType.CONNECTION);
            this.modalConfig.openMessageModal("exit_while_offline");
            return;
        }

        // TODO: this code will never trigger because we treat this "handled" error as a success case and will
        //      call successfulUpload not unsuccessfulUpload
        if (this.isTrainingSessionException(result)) {
            this.log.error("isTrainingSessionException Error. Reason: " + result.status);
            this.modalConfig.openMessageModal("noactive_session");
            return;
        }

        if (this.shouldDiscardItem(queueItem, result)) {
            this.log.log("Skipping item because we already have uploaded the data, or is a glossary transition timing issue");
            this.successfulUpload(queueItem, result);
        } else if (this.shouldRetryItem(queueItem, result)) {
            this.log.log("Upload failure may be recoverable. Retrying upload.");
            this.retryFailedUpload(queueItem);
        } else {
            this.log.error("Upload queue is resolving a non-recoverable failure, queueItem url = "
                + queueItem.url + " data = " + this.getDataString(queueItem), ErrorType.CONNECTION);
            this._allowUpload = false;
            this._allowRetry = false;
            let handled = false;
            try {
                if (!queueItem.isAsync) {
                    queueItem.subject.error("error status: " + result.status);
                    handled = true;
                }
                if (typeof queueItem.failure === "function") {
                    queueItem.failure(result.status);
                    handled = true;
                }
            } catch (e1) {
                this.log.error("Error during upload queue failure handler: " + e1.name, ErrorType.CONNECTION);
            } finally {
                this.uploading = false;
                this.uploadQueue = [];
                if (!handled) {
                    this.log.error("Upload queue is resolving a non-recoverable failure, queueItem url = "
                        + queueItem.url + " data = " + this.getDataString(queueItem), ErrorType.CONNECTION);
                    this.modalConfig.openMessageModal("disconnect_network_error");
                }
            }
        }
    }

    /**
     * Check for special conditions that allow us to just skip a failed queue item
     */
    private shouldDiscardItem(queueItem: QueueItem, result: APIResult): boolean {
        if (result.status === 500) {
            if (queueItem.data && queueItem.data.bookSessionJson
                && queueItem.data.bookSessionJson.includes("activityType")
                && queueItem.data.bookSessionJson.includes(Scilearn.__GLOSSARY__))
            {
                this.log.error("Discarded glossary timing event due to 500 error", ErrorType.CONNECTION);
                return true;
            }
            if (result.data) {
                // Sometimes a connection closes during response but server already has stored the sentence data.
                // Check for "Upload recording failed because of wrong sentence id X, expected id is (Y > X)"
                let match = UploadService.WRONG_SENTENCE_ID_REGEXP.exec(result.data as string);
                if (Array.isArray(match) && match.length >= 3) {
                    let X = parseInt(match[1], 10); // sent id
                    let Y = parseInt(match[2], 10); // expected id
                    if (Y - X > 0) {
                        // server already has sentence X's data, so we can discard this item
                        this.log.log("Discarding QueueItem: " + queueItem.url + " " + queueItem.type);
                        return true;
                    }
                }
            }
        }
        return false;
    }

    /**
     * Check whether to retry the failure or not
     */
    private shouldRetryItem(queueItem: QueueItem, result: APIResult): boolean {

        this.updateOfflineStatus();

        /* if (!this.networkService.isOnline() && Date.now() - this.offLineStartTime > UploadService.OFFLINE_MAX_TIME) {
         this.log.log("Timeout due to too long in offline status.");
         return false;
         }*/

        if (queueItem.retries >= UploadService.MAX_RETRIES) {
            this.log.error("Timeout due to too many retries of same data.", ErrorType.CONNECTION);
            return false;
        }

        if (result.status === 500) {
            this.log.error("Error status is 500, assuming non-recoverable.", ErrorType.CONNECTION);
            return false;
        }

        if (result.status === 404) {
            this.log.error("Error status is 404, assuming non-recoverable.", ErrorType.CONNECTION);
            return false;
        }

        return this._allowRetry;
    }

    private retryFailedUpload(failedQueueItem: QueueItem): void {

        if (!failedQueueItem) {
            this.log.log("Upload queue is empty. Nothing to retry.");
            this.uploading = false;
            return;
        }

        if (this.offLineStartTime === undefined) {
            // if we're offline, we don't count this against the retries limit per call.
            failedQueueItem.retries++;
        }

        setTimeout(() => this.uploadFirstQueueItem(), UploadService.UPLOAD_RETRY_INTERVAL);
    }

    private getDataString(queueItem: QueueItem): string {
        let data = "";
        try {
            data = JSON.stringify(queueItem.data);
        } catch (e) {
            // don't expect this to happen, but just in case
            this.log.error("Could not stringify queueItem data: " + e.message);
        }
        return data;
    }

    /**
     * (synchronous, will not resolve until success or failure is known)
     */
    public startTrainingSession(sessionData: any, url: string, synchronous?: boolean): Observable<Object> {

      if (this.config.isSuperUserMode) {
            return of("OK");
        }

      if (!this._allowUpload) {
            this.modalConfig.openMessageModal("upload_error");
            return throwError("allowUpload is false");
        }

      let sessionItem = {
            type: TYPE.START_SESSION,
            url,
            data: sessionData,
            synchronous: !!synchronous,
            result: undefined,
            isAsync: false,
            retries: 0,
            success: undefined,
            failure: undefined,
            subject: undefined
        } as QueueItem;

      return this.addQueueItem(sessionItem);
    }

    public closeTrainingSession(url: string, synchronous?: boolean): Observable<Object> {
        if (this.config.isSuperUserMode) {
            return of("OK");
        }

        if (!this._allowUpload) {
            this.modalConfig.openMessageModal("upload_error");
            return throwError("allowUpload is false");
        }

        let sessionItem = {
            type: TYPE.CLOSE_SESSION,
            url,
            data: {},
            synchronous: !!synchronous,
            result: undefined,
            isAsync: false,
            retries: 0,
            success: undefined,
            failure: undefined,
            subject: undefined
        } as QueueItem;

        return this.addQueueItem(sessionItem);
    }

    public keepAlive(url: string): Observable<Object> {
        if (this.config.isSuperUserMode) {
            return of("OK");
        }

        if (!this._allowUpload) {
            this.modalConfig.openMessageModal("upload_error");
            return throwError("allowUpload is false");
        }

        // KeepAlives are not queued
        return this.putData(url).pipe(map(x => x.data));
    }

    public startBookSession(sessionData: any, url: string, synchronous?: boolean): Observable<Object> {
        if (this.config.isSuperUserMode) {
            return of("OK");
        }

        if (!this._allowUpload) {
            this.modalConfig.openMessageModal("upload_error");
            return throwError("allowUpload is false");
        }

        let sessionItem = {
            type: TYPE.START_BOOK_SESSION,
            url,
            data: sessionData,
            synchronous: !!synchronous,
            result: undefined,
            isAsync: false,
            retries: 0,
            success: undefined,
            failure: undefined,
            subject: undefined
        } as QueueItem;

        this.log.log("StartBookSession: " + sessionData.storyUniqName);

        if (this.lastOpenBookSession === sessionData.storyUniqName) {
            this.log.info("!!! DUPLICATE StartBookSession: sessionData=" + JSON.stringify(sessionData));
        }
        this.lastOpenBookSession = sessionData.storyUniqName;

        return this.addQueueItem(sessionItem);
    }

    public closeBookSession(url: string, synchronous?: boolean): Observable<Object> {


        if (this.config.isSuperUserMode) {
            return of("OK");
        }

        if (!this._allowUpload) {
            this.modalConfig.openMessageModal("upload_error");
            return throwError("allowUpload is false");
        }

        let sessionItem = {
            type: TYPE.CLOSE_BOOK_SESSION,
            url,
            data: {},
            synchronous: !!synchronous,
            result: undefined,
            isAsync: false,
            retries: 0,
            success: undefined,
            failure: undefined,
            subject: undefined
        } as QueueItem;

        this.log.log("CloseBookSession: " + this.lastOpenBookSession);
        this.lastOpenBookSession = "";

        return this.addQueueItem(sessionItem);
    }

    /**
     * @returns Observable
     */
    public modifyBookSession(bookSessionData, isAsync, successCB?, failureCB?): Observable<string> {
      if (this.config.isSuperUserMode) {
            if (successCB) {
                successCB();
            }
            return of("OK");
        }

      if (!this._allowUpload) {
            return throwError("allowUpload is false");
        }
      if (failureCB) {
            console.log("adding queue item with failure cb of type:" + (typeof failureCB));
        } else {
            console.log("error callback not defined");
        }

      let queueItem = {
            type: TYPE.MODIFY_BOOK_SESSION,
            url: this.config.userModelPath + "activity",
            data: {tenantId: this.login.tenantId, bookSessionJson: JSON.stringify(bookSessionData)},
            result: undefined,
            synchronous: false,
            isAsync: !!isAsync,
            success: successCB,
            failure: failureCB,
            retries: 0,
            subject: undefined
        } as QueueItem;

      return this.addQueueItem(queueItem);
    }

    public clearFailureState(): void {
        this._allowUpload = true;
    }

}

enum TYPE {START_BOOK_SESSION, MODIFY_BOOK_SESSION, CLOSE_BOOK_SESSION,
           START_SESSION, CLOSE_SESSION}

class QueueItem {
    type: TYPE;
    url: string;
    data: any;
    synchronous: boolean;
    result: APIResult;
    isAsync: boolean;
    success: Function;
    failure: Function;
    retries: number;
    subject: Subject<object|string>;
}

export class APIResult {
    data: object|string;
    success: boolean;
    status: number;
}

class SLCResponse {
    StatusCode: number;
    Description: string;
}
